import { useState } from 'react';
import dayjs from 'dayjs';
import type { Message } from '@/types/domain';
import { resolveMediaUrl } from '@/utils/media';
import { OrderProposalCard } from './OrderProposalCard';
import { InlineReviewForm } from './InlineReviewForm';
import styles from './MessageBubble.module.css';

export interface MessageBubbleProps {
  message: Message;
  isMine: boolean;
  /** Wired by ChatThreadPage for the client side; specialist sees status only. */
  onAcceptOrder?: (orderId: number) => void;
  onDeclineOrder?: (orderId: number) => void;
  /**
   * F-180 A9 — failed optimistic bubbles invoke this on tap to retry
   * delivery. Only wired for `message.status === 'failed'`.
   */
  onRetry?: (message: Message) => void;
}

// F-170 B0.6 / B2.4: `message.order.status_label` is now the canonical
// UA label (computed server-side). Local map stays as a fallback for
// back-compat with payloads that predate the addition.
const STATUS_LABEL: Record<string, string> = {
  // F-181: `created` = client-initiated, waits for specialist confirm.
  created: 'Замовлення створено',
  proposed: 'Запропоновано',
  accepted: 'Прийнято',
  in_progress: 'В роботі',
  completed: 'Виконано',
  cancelled: 'Скасовано',
  reviewed: 'З відгуком',
};

export function MessageBubble({
  message,
  isMine,
  onAcceptOrder,
  onDeclineOrder,
  onRetry,
}: MessageBubbleProps): JSX.Element {
  if (message.kind === 'system') {
    const text =
      typeof message.system_payload?.text === 'string'
        ? message.system_payload.text
        : 'Системне повідомлення';
    return (
      <div className={styles.row}>
        <div className={[styles.bubble, styles.system].join(' ')}>{text}</div>
      </div>
    );
  }
  if (message.kind === 'order_proposal' && message.order) {
    const order = message.order;
    // F-181: both `proposed` (specialist→client) and `created`
    // (client→specialist) show Прийняти/Скасувати to the RECEIVING side.
    const canAct = !isMine && (order.status === 'proposed' || order.status === 'created');
    return (
      <div
        className={[styles.row, isMine && styles.mine, styles.orderRow]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.orderWrapper}>
          <OrderProposalCard
            order={order}
            canAccept={canAct}
            canDecline={canAct}
            onAccept={canAct && onAcceptOrder ? () => onAcceptOrder(order.id) : undefined}
            onDecline={canAct && onDeclineOrder ? () => onDeclineOrder(order.id) : undefined}
          />
          <div className={styles.orderMeta}>
            <span>{dayjs(message.created_at).format('HH:mm')}</span>
            {isMine ? <span aria-hidden>{message.is_read ? '✓✓' : '✓'}</span> : null}
          </div>
        </div>
      </div>
    );
  }
  if (message.kind === 'order_review_request') {
    // F-170 B0.15 / B4.7: inline review (or "помилково закрито" dispute)
    // from the client; specialist just sees the prompt text so they know
    // a review was requested. After submit/dispute we collapse to a
    // read-only confirmation in local state — the backend will follow
    // up with a new chat message to make the change persistent across
    // refreshes.
    return (
      <InlineReviewBubble
        message={message}
        isMine={isMine}
      />
    );
  }
  if (message.kind === 'order_status') {
    const label = message.order
      ? message.order.status_label || STATUS_LABEL[message.order.status] || message.order.status
      : '';
    const text =
      message.text ||
      (message.order ? `Замовлення «${message.order.title}» · ${label}` : 'Статус замовлення');
    return (
      <div className={styles.row}>
        <div className={[styles.bubble, styles.system].join(' ')}>{text}</div>
      </div>
    );
  }
  // F-180 A9 — failed optimistic bubbles render as a tap-target with
  // greyed text and a clear retry affordance. Pending bubbles dim
  // slightly so the user gets feedback that the send is in flight.
  const isFailed = isMine && message.status === 'failed';
  const isPending = isMine && message.status === 'pending';
  return (
    <div className={[styles.row, isMine && styles.mine].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.bubble}
        style={{
          opacity: isFailed ? 0.6 : isPending ? 0.7 : 1,
          border: isFailed ? '1px solid #dc2626' : undefined,
          cursor: isFailed && onRetry ? 'pointer' : 'default',
          background: isFailed ? '#fef2f2' : undefined,
          color: isFailed ? '#7f1d1d' : undefined,
          textAlign: 'left',
          width: '100%',
        }}
        onClick={() => {
          if (isFailed && onRetry) onRetry(message);
        }}
        disabled={!isFailed || !onRetry}
        aria-label={isFailed ? 'Не доставлено. Натисни щоб повторити.' : undefined}
      >
        {message.kind === 'image' && message.image_url ? (
          <img src={resolveMediaUrl(message.image_url)} alt="" className={styles.image} />
        ) : null}
        {message.text ? <div>{message.text}</div> : null}
        <div className={styles.meta}>
          <span>{dayjs(message.created_at).format('HH:mm')}</span>
          {isFailed ? (
            <span aria-hidden>· Натисни щоб повторити</span>
          ) : isPending ? (
            <span aria-hidden>· …</span>
          ) : isMine ? (
            <span aria-hidden>{message.is_read ? '✓✓' : '✓'}</span>
          ) : null}
        </div>
      </button>
    </div>
  );
}

/** Inner state-holder for the review/dispute message — kept inline so
 *  parent doesn't have to thread submit/dispute callbacks down. */
function InlineReviewBubble({
  message,
  isMine,
}: {
  message: Message;
  isMine: boolean;
}): JSX.Element {
  const [state, setState] = useState<'idle' | 'submitted' | 'disputed'>('idle');
  const orderId =
    message.order?.id ??
    (typeof message.system_payload?.order_id === 'number'
      ? (message.system_payload.order_id as number)
      : null);
  // Specialist side only sees the prompt text — they can't review themselves.
  if (isMine || orderId == null) {
    return (
      <div className={styles.row}>
        <div className={[styles.bubble, styles.system].join(' ')}>
          {message.text || 'Очікуємо на відгук клієнта'}
        </div>
      </div>
    );
  }
  if (state === 'submitted') {
    return (
      <div className={styles.row}>
        <div className={[styles.bubble, styles.system].join(' ')}>Відгук залишено · дякую!</div>
      </div>
    );
  }
  if (state === 'disputed') {
    return (
      <div className={styles.row}>
        <div className={[styles.bubble, styles.system].join(' ')}>
          Оскаржено — чекай на відповідь майстра
        </div>
      </div>
    );
  }
  return (
    <div className={styles.row}>
      <InlineReviewForm
        orderId={orderId}
        onSubmitted={() => setState('submitted')}
        onDisputed={() => setState('disputed')}
      />
    </div>
  );
}
